"use client";
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _ReadyNotifier_instances, _ReadyNotifier_status, _ReadyNotifier_emit, _a;
import { RudderAnalytics } from "@rudderstack/analytics-js/bundled";
/**
 * analytics is a function that initializes the analytics package and returns
 * an object with methods to track events, page views, and identify users. Usable
 * in browser environments but using the <AnalyticsInit /> and analyticsClient
 * function instead in React environments is recommended instead.
 */
export const analytics = ({ writeKey, dataPlaneUrl, cookieDomain, configUrl, applicationName, amplitude = false, debug = false, }) => {
    if (!writeKey) {
        throw new Error("Missing writeKey");
    }
    else if (!dataPlaneUrl) {
        throw new Error("Missing dataPlaneUrl");
    }
    else if (!configUrl) {
        throw new Error("Missing configUrl");
    }
    else if (!cookieDomain) {
        throw new Error(`Invalid cookieDomain`);
    }
    validateCookieDomain(cookieDomain);
    const rs = new RudderAnalytics();
    rs.load(writeKey, dataPlaneUrl, {
        setCookieDomain: cookieDomain,
        sameDomainCookiesOnly: true,
        configUrl: configUrl,
    });
    if (debug) {
        console.info("%cAnalytics Initialized:", "font-weight: 800", JSON.stringify({
            writeKey,
            dataPlaneUrl,
            configUrl,
            cookieDomain,
            applicationName,
            amplitude,
        }, null, "  "));
    }
    return {
        identify: (userId, traits, options, callback) => {
            if (debug)
                debugLogger({ userId, traits, options });
            rs.identify(userId, traits, options, callback);
        },
        track: (event, properties, options, amplitudeEvent, cb) => {
            const amplitudeSetting = typeof amplitudeEvent === "boolean" ? amplitudeEvent : amplitude;
            const props = { ...properties, eventAction: event };
            const opts = {
                integrations: { Amplitude: amplitudeSetting },
                ...options,
            };
            if (debug)
                debugLogger({ event, properties: props, options: opts });
            return rs.track(applicationName, props, opts, cb);
        },
        _track: rs.track,
        page: rs.page,
        group: rs.group,
        getAnonymousId: rs.getAnonymousId,
        setAnonymousId: rs.setAnonymousId,
        getUserId: rs.getUserId,
        consent: rs.consent,
        reset: rs.reset,
        ready: async () => new Promise((r) => rs.ready(r)),
    };
};
function validateCookieDomain(cookieDomain) {
    if (cookieDomain.startsWith("http://localhost"))
        return;
    const subdomainParts = cookieDomain.split(".").slice(0, -2);
    if (subdomainParts.every((e) => e === "")) {
        // prevent cookies leaking to top level domains such as "sanity.io" and ".sanity.work"
        throw new Error("Invalid cookieDomain parameter, provide entire URL with valid specific subdomain");
    }
}
function debugLogger({ event, userId, properties, traits, options, }) {
    console.groupCollapsed(`%c[Analytics] ${userId ? "Identify" : "Track"}`, "color: gray", event);
    if (event)
        console.info("%cEvent:", "font-weight: 800", event);
    if (userId)
        console.info("%cUserId:", "font-weight: 800", userId);
    if (properties) {
        console.info("%cProperties:", "font-weight: 800", JSON.stringify(properties, null, "  "));
    }
    if (traits) {
        console.info("%cTraits:", "font-weight: 800", JSON.stringify(traits, null, "  "));
    }
    if (options) {
        console.info("%cOptions:", "font-weight: 800", JSON.stringify(options, null, "  "));
    }
    console.groupEnd();
}
// for client components
let _analytics = null;
let _initQueue = [];
const statusInstance = new (_a = class ReadyNotifier extends EventTarget {
        constructor() {
            super(...arguments);
            _ReadyNotifier_instances.add(this);
            _ReadyNotifier_status.set(this, "none");
        }
        set(status) {
            __classPrivateFieldSet(this, _ReadyNotifier_status, status, "f");
            __classPrivateFieldGet(this, _ReadyNotifier_instances, "m", _ReadyNotifier_emit).call(this);
        }
        get() {
            return __classPrivateFieldGet(this, _ReadyNotifier_status, "f");
        }
    },
    _ReadyNotifier_status = new WeakMap(),
    _ReadyNotifier_instances = new WeakSet(),
    _ReadyNotifier_emit = function _ReadyNotifier_emit() {
        this.dispatchEvent(new CustomEvent("statusChange", { detail: __classPrivateFieldGet(this, _ReadyNotifier_status, "f") }));
    },
    _a)();
/**
 * AnalyticsInit is a function that initializes the analytics package and
 * provides the instance to the rest of the application.
 * Capitalized for easier JSX rendering
 */
export function AnalyticsInit({ config }) {
    const initStatus = statusInstance.get();
    if (initStatus === "done" || initStatus === "error") {
        return null;
    }
    if (typeof window === "object" && !_analytics && initStatus === "none") {
        try {
            statusInstance.set("pending");
            _analytics = analytics(config);
            _analytics.ready().then(() => {
                statusInstance.set("done");
                _initQueue.forEach((f) => f(_analytics));
            });
        }
        catch (e) {
            statusInstance.set("error");
            console.error("Failed to initialize analytics package");
        }
    }
    return null;
}
const _noop = () => { };
const _proxy = new Proxy({}, {
    get: function (_, prop, receiver) {
        const initStatus = statusInstance.get();
        if (prop === "isReady") {
            return initStatus === "done" && _analytics !== null;
        }
        if (prop === "ready" && initStatus !== "done") {
            // if not ready return a promise that will resolve/reject when ready
            return () => new Promise((res, rej) => {
                // check initial state
                if (statusInstance.get() === "error") {
                    return rej();
                }
                const callback = (e) => {
                    const status = e?.detail;
                    if (status === "done" && _analytics) {
                        res(Reflect.get(_analytics, prop, receiver));
                        statusInstance.removeEventListener("statusChange", callback);
                    }
                    else if (status === "error") {
                        rej();
                        statusInstance.removeEventListener("statusChange", callback);
                    }
                };
                statusInstance.addEventListener("statusChange", callback);
            });
        }
        if (statusInstance.get() === "pending" || statusInstance.get() === "none") {
            // await for load
            _initQueue.push((analytics) => Reflect.get(analytics, prop, receiver));
            return _noop;
        }
        if (statusInstance.get() === "error" || !_analytics) {
            console.error("Analytics package failed to initialize");
            return _noop;
        }
        return Reflect.get(_analytics, prop, receiver);
    },
});
/**
 * analyticsClient is a proxy object that provides access to the analytics
 * instance. It will queue pending events if the analytics package is not
 * initialized yet.
 */
export const analyticsClient = _proxy;
//# sourceMappingURL=index.js.map